(ns ui.gallery.query
  "The queries of the Replicant gallery and how their results are read
   from the store."
  (:require [parts.replicant.query :as query]
            [ui.gallery.layout :as layout]))

(defn gallery-query
  "The `:query/gallery` for the current folder, mode and loaded window
   of the gallery state. `:query/refresh? true` makes the db-view
   notify loop (`ui.notify`) refresh it, so uploads that finished or
   moves made in another browser tab show up without a reload."
  [state]
  (let [{:keys [folder mode offset page-size]} (:gallery state)]
    {:query/kind :query/gallery
     :query/refresh? true
     :query/data (cond-> {:gallery/mode (or mode :active)
                          :gallery/offset (or offset 0)
                          :gallery/page-size (or page-size layout/min-page-size)}
                   folder (assoc :gallery/folder folder))}))

(defn gallery-query?
  [q]
  (= :query/gallery (:query/kind q)))

(defn result
  "The value the gallery renders: the result of the current gallery
   query, or the last result while the current one is still loading, so
   the grid never flashes empty when the loaded window changes."
  [state]
  (or (query/get-result state (gallery-query state))
      (get-in state [:gallery :last-result])))

(defn update-result
  "Applies `f` (with `args`) to every result the gallery may render
   (see `result`): the results in the log of the current gallery query
   and the `:last-result`. For the optimistic updates after a move
   (`ui.gallery.move`), which show the move right away instead of
   waiting for the refresh."
  [state f & args]
  (let [q (gallery-query state)
        update-result* (fn [r]
                         (apply f r args))]
    (-> state
        (update :ui.query/log
                (fn [log]
                  (if (contains? log q)
                    (update log
                            q
                            (fn [entries]
                              (mapv (fn [entry]
                                      (cond-> entry
                                        (:query/result entry)
                                        (update :query/result update-result*)))
                                    entries)))
                    log)))
        (update :gallery
                (fn [gallery]
                  (cond-> gallery
                    (:last-result gallery)
                    (update :last-result update-result*)))))))

(defn loading?
  [state]
  (query/loading? state (gallery-query state)))

(defn error?
  [state]
  (query/error? state (gallery-query state)))

(defn current-folder
  "The uuid of the folder the gallery shows (nil before the first
   result arrived)."
  [state]
  (get-in (result state) [:folder-tree :current-folder]))

(defn eids-query
  "The `:query/gallery-eids` for the current view, optionally restricted
   to the range between two eids."
  ([state]
   (eids-query state nil))
  ([state [range-from range-to]]
   (let [{:keys [folder mode]} (:gallery state)]
     {:query/kind :query/gallery-eids
      :query/data (cond-> {:gallery/mode (or mode :active)}
                    folder (assoc :gallery/folder folder)
                    (and range-from range-to) (assoc :gallery/range-from range-from
                                                     :gallery/range-to range-to))})))

(defn entry-query
  [story-media-eid]
  {:query/kind :query/gallery-entry
   :query/refresh? true
   :query/data {:story-media/eid story-media-eid}})

(defn entry-query?
  [q]
  (= :query/gallery-entry (:query/kind q)))

(defn folder-delete-preview-query
  [folder-uuid]
  {:query/kind :query/gallery-folder-delete-preview
   :query/data {:folder/uuid folder-uuid}})

(defn prune-log
  "Removes all queries for which `stale?` is true, except `keep`, from
   the query log, so the log does not grow with every scroll position
   and the notify loop only refreshes the current query."
  [log stale? keep]
  (into {}
        (remove (fn [[q _]]
                  (and (stale? q)
                       (not= q keep))))
        log))
